import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import SearchBar from '../components/SearchBar';
import IconButton from '../components/IconButton';
import PrimaryButton from '../components/PrimaryButton';
import StatusBadge from '../components/StatusBadge';
import userService from '../services/userService';
import RepAccessModal from '../components/RepAccessModal';
import { refId } from '../utils/repTree';
import { useNavigate } from 'react-router-dom';
import { useT, useLocale } from '../i18n/LanguageContext';

function SalesRepCard({ rep, onEdit, onAccess, accountCount, repCount }) {
  const t = useT();
  const locale = useLocale();
  const firstName = rep.firstName || '';
  const lastName = rep.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const name = `${firstName} ${lastName}`.trim();

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-hidden">
      <div className="p-5">
        {/* Give the identity its own row so names stay readable at narrow card widths.
            Keep the status and actions together below it. */}
        <div className="flex flex-col gap-2 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-[34px] h-[34px] shrink-0 rounded-full bg-[#e6f1ff] flex items-center justify-center text-[13px] font-semibold text-[#0f172a]">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-5 font-semibold text-[#0f172a] whitespace-normal break-words" title={name}>{name}</p>
              <p className="text-xs font-medium text-[#64748b] truncate" title={rep.licenseNumber || rep.email}>
                {rep.licenseNumber || rep.email}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 shrink-0">
            <StatusBadge status={rep.isActive !== false ? 'active' : 'inactive'} size="small" />
            <button
              onClick={() => onAccess && onAccess(rep)}
              className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-gray-100 transition-colors"
              title={t('Access')}
              aria-label={t('Access for {name}', { name: `${rep.firstName || ''} ${rep.lastName || ''}`.trim() }).trim()}
            >
              <span className="material-symbols-outlined text-[#64748b] text-[18px]">key</span>
            </button>
            <button
              onClick={() => onEdit && onEdit(rep)}
              className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-gray-100 transition-colors"
              title={t('Edit')}
            >
              <span className="material-symbols-outlined text-[#64748b] text-[18px]">edit</span>
            </button>
          </div>
        </div>

        {/* What this rep can see, at a glance. The key button above is for CHANGING it; this is
            for reading it without opening anything, which is what an admin scanning the page wants. */}
        <div className="flex items-center gap-1 mb-1.5 min-w-0">
          <span className="material-symbols-outlined text-[#64748b] text-[18px] shrink-0">visibility</span>
          <span className="text-xs font-medium text-[#64748b] truncate">
            {t(accountCount === 1 ? 'Sees {n} account' : 'Sees {n} accounts', { n: accountCount })}
            {repCount > 0 && ' · ' + t(repCount === 1 ? '{n} other rep' : '{n} other reps', { n: repCount })}
          </span>
        </div>

        <div className="flex items-center gap-1 mb-4">
          <span className="material-symbols-outlined text-[#64748b] text-[18px]">location_on</span>
          <span className="text-xs font-medium text-[#64748b]">
            {rep.address?.city && rep.address?.state
              ? `${rep.address.city}, ${rep.address.state}`
              : rep.address?.street
                ? [rep.address.street, rep.address.city, rep.address.state].filter(Boolean).join(', ')
                : t('N/A')}
          </span>
        </div>

        <div className="h-px bg-[#e2e8f0] mb-4" />

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <div>
            <p className="text-[10px] font-medium text-[#64748b]">{t('Orders')}</p>
            <p className="text-base font-semibold text-[#0f172a]">{rep.orderCount || 0}</p>
          </div>
          <div>
            <p className="text-[10px] font-medium text-[#64748b]">{t('IVR Request')}</p>
            <p className="text-base font-semibold text-[#0f172a]">{rep.ivrCount || 0}</p>
          </div>
          <div>
            <p className="text-[10px] font-medium text-[#64748b]">{t('Practitioners')}</p>
            <p className="text-base font-semibold text-[#0f172a]">{rep.practitionerCount || 0}</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-2.5 bg-[rgba(226,232,240,0.2)] border-t border-[#e2e8f0] flex items-center gap-1.5">
        <span className="material-symbols-outlined text-[#64748b] text-[16px]">calendar_today</span>
        <span className="text-[10px] font-medium text-[#64748b]">
          {t('Joined: {date}', {
            date: rep.createdAt
              ? new Date(rep.createdAt).toLocaleDateString(locale, { month: 'short', year: 'numeric' })
              : t('N/A'),
          })}
        </span>
      </div>
    </div>
  );
}

export default function SalesRepresentatives() {
  const t = useT();
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // The card list is paginated and search-filtered; the hierarchy is not. Keeping them separate
  // means a card can name a manager who is not on the current page - which is the normal case.
  const [allReps, setAllReps] = useState([]);
  const [structureVersion, setStructureVersion] = useState(0);
  // Access is edited per person, from the key on that person's card.
  const [accessRep, setAccessRep] = useState(null);

  useEffect(() => {
    async function fetchReps() {
      setLoading(true);
      try {
        const params = { role: 'sales_rep' };
        if (search) params.search = search;
        const res = await userService.getUsers(params);
        const data = res.data?.data?.data || [];
        setReps(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch sales reps.');
      } finally {
        setLoading(false);
      }
    }
    fetchReps();
  }, [search]);

  useEffect(() => {
    userService
      .getRepAccess()
      .then((res) => {
        const data = res.data?.data?.data || res.data?.data || [];
        setAllReps(Array.isArray(data) ? data : []);
      })
      .catch(() => setAllReps([]));
  }, [structureVersion]);

  // The caption comes from the ACCESS rows, never from the card row. The card list is a separate,
  // paginated fetch that is NOT re-read when the access dialog saves - counting off it would leave
  // a rep captioned with their old access until the next full page load.
  const accessRow = (rep) => allReps.find((r) => String(r._id) === String(rep._id));
  const accountCountOf = (rep) => (accessRow(rep)?.clinics || []).length;
  const repCountOf = (rep) => (accessRow(rep)?.visibleReps || []).filter((v) => refId(v)).length;

  return (
    <div>
      <PageHeader
        title="Sales Representatives"
        subtitle="Manage registered sales representatives, their teams and their performance."
      >
        <SearchBar
          placeholder="Search by name, license..."
          className="w-[243px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton icon="upload" />
        <PrimaryButton onClick={() => navigate('/registrations')}>{t('+ Add User')}</PrimaryButton>
      </PageHeader>

      <RepAccessModal
        open={!!accessRep}
        rep={accessRep}
        onClose={() => setAccessRep(null)}
        onSaved={() => setStructureVersion((v) => v + 1)}
      />

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
        </div>
      ) : reps.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#64748b]">{t('No sales representatives found')}</div>
      ) : (
        // As many columns as fit at 260px each - four on a wide monitor, three or two on a laptop,
        // one on a phone - instead of four columns squeezed into whatever width there is.
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5">
          {reps.map((rep) => (
            <SalesRepCard
              key={rep._id}
              rep={rep}
              accountCount={accountCountOf(rep)}
              repCount={repCountOf(rep)}
              onAccess={(r) => setAccessRep(r)}
              onEdit={(r) => navigate(`/registrations/${r._id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
